import { createRootRoute, Outlet, useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';

import { useSession } from '@platform/api-client/react';
import { applyDocumentBranding, Logo, ToastProvider, useDocumentTitle } from '@platform/ui';

import '../booking/booking.css';
import { useFlags } from '../flags';
import { IntakeRuntimeProvider, intakeRuntime } from '../lib/api/intake-client-context';
import { PaymentsRuntimeProvider, paymentsRuntime } from '../lib/api/payments-client-context';
import { pageTitle } from '../lib/page-title';
import { applyTenantBranding, brandVersionOf, clinicName, useTenant } from '../lib/tenant/tenant';
import { APP_BUILD } from '../version';

export const Route = createRootRoute({
  component: RootLayout,
});

/**
 * Which live page each route is the parity target for. The three live pages are NOT
 * interchangeable shells, so the differences are data here rather than three copies of
 * the markup:
 *
 *  - `embed`  — `/payment`'s markup is wrapped in Webflow's `.w-embed` div, which is a
 *    shrink-to-fit flex item, so on that page the white header band and the footer are
 *    540px wide and centred instead of spanning the viewport. Captured:
 *    `payment/computed.json` .header w=540 against `book/computed.json` .header w=1280.
 *  - `font`   — only `/payment` links DM Sans, so the other pages render in the system
 *    UI font. `funnel.fontFix` turns that live bug off (ADR-008, flags.ts).
 *  - `footer` — `/payment` says "Questions? Contact us at …", the others "Questions? …".
 */
interface ShellSpec {
  embed: boolean;
  dmSans: boolean;
  contactPrefix: boolean;
}

const SHELLS: Record<string, ShellSpec> = {
  '/payment': { embed: true, dmSans: true, contactPrefix: true },
};

const DEFAULT_SHELL: ShellSpec = { embed: false, dmSans: false, contactPrefix: false };

/**
 * The five routes FR-BOOK-01 measures against the live site. Everything else in this app —
 * the five auth screens and the nine portal screens — is ours, was never on heyallergy.com
 * and has no pixel bar to clear.
 *
 * That distinction is what lets M13.7 turn the accessible colour set ON for those screens
 * without a flag (below). The funnel keeps the live palette until the owner signs off on
 * Q-P15, and `funnel.a11yContrast` is still the only thing that changes it there.
 */
const PARITY_ROUTES = new Set(['/', '/book', '/insurance', '/payment', '/paperwork']);

/**
 * The booking funnel's shell (tokens doc §7.8): a white header with the logo centred and
 * a 1px neutral-200 bottom border, the page on `--color-canvas`, content capped at
 * 540px, and the quiet 12px footer — which the Webflow bundle paints white and gives a
 * hairline top border, so the parity build does too (`booking.css`, webflow-survivor).
 *
 * `<Logo variant="tenant" />` so per-tenant branding (FR-PLAT-03) stays a CSS variable
 * assignment at bootstrap and nothing here has to change.
 */
function RootLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const flags = useFlags();
  const shell = SHELLS[pathname] ?? DEFAULT_SHELL;
  const dmSans = flags.funnel.fontFix || shell.dmSans;
  /*
   * M13.7. The auth and portal screens share this shell — and therefore the funnel's
   * `.footer`, whose grey is 2.56:1 and whose "HeyAllergy.com" link is told apart from the
   * sentence around it by hue alone (84 axe nodes across fourteen routes). The fix is not
   * a new stylesheet: `[data-a11y="on"]` already re-points `--gray-400` and `--ha-blue` to
   * their accessible siblings and underlines the footer link, and on a screen that is not
   * a parity target there is nothing to hold it back. So it is on, always, off the funnel.
   */
  const accessiblePalette = flags.funnel.a11yContrast || !PARITY_ROUTES.has(pathname);

  /*
   * Boot (M2.9). Two calls, both fired here so every screen below can read them from
   * cache instead of starting its own waterfall:
   *
   *   GET /api/v1/auth/session  — who is signed in. Always 200; anonymous is a state,
   *                               not an error, so nothing here branches on failure.
   *   GET /api/v1/public/tenant — which clinic this host is, and what it looks like.
   *
   * The tenant's brand is applied as CSS custom properties on :root, which is the whole
   * of the theming contract (packages/ui/README.md). It is an effect rather than a
   * render because it writes to the document, and it re-runs only when the payload
   * itself changes.
   */
  useSession();
  const tenant = useTenant();

  const title = pageTitle(pathname, clinicName(tenant.data));
  useDocumentTitle(title);

  useEffect(() => {
    if (tenant.data === undefined) return;
    applyTenantBranding(tenant.data);
  }, [tenant.data]);

  /*
   * M17.3: the parts of the brand that live in `<head>` — the favicon (a versioned URL, so
   * a new upload is picked up on the next payload) and the Open Graph site name and title.
   * Runtime only: a crawler reads index.html and does not run this (scope design §3.3).
   */
  const faviconUrl = tenant.data?.branding.faviconUrl ?? null;
  const brandVersion = brandVersionOf(tenant.data);
  const name = clinicName(tenant.data);
  useEffect(() => {
    if (tenant.data === undefined) return;
    applyDocumentBranding({ clinicName: name, faviconUrl, pageTitle: title, brandVersion });
  }, [tenant.data, name, faviconUrl, title, brandVersion]);

  /*
   * M18.2: `/embed/*` is loaded inside the mobile app's WebView and renders nothing but
   * itself — no header, no footer, no funnel wrapper and none of `booking.css`'s parity
   * classes around it. The payments runtime is still installed here, because the card
   * form is exactly what that route exists to show.
   */
  if (pathname.startsWith('/embed/')) {
    return (
      <PaymentsRuntimeProvider runtime={paymentsRuntime}>
        <Outlet />
      </PaymentsRuntimeProvider>
    );
  }

  const body = (
    <>
      <div className="header">
        <div className="header-logo">
          <Logo variant="tenant" height={36} label={clinicName(tenant.data)} />
        </div>
      </div>

      {/*
       * M13.7. `<main>` rather than `<div>`: the funnel, the auth screens and the nine
       * portal screens all render inside this one shell, and none of them had a main
       * landmark — a screen reader had no way to skip the header or to jump to the
       * content, and every page failed `landmark-one-main`. The element, the class and
       * therefore every pixel are otherwise unchanged, so the parity build is unaffected
       * (FR-BOOK-01, ADR-008); `compare.mjs` is the proof and its numbers did not move.
       *
       * No skip link: WCAG 2.4.1 asks for a way past *blocks of repeated content*, and
       * this shell's header is one logo. The clinic admin and the platform console, whose
       * header is followed by sixteen navigation links, get one in `AppShell`.
       */}
      <main className="main">
        <Outlet />
      </main>

      <div className="footer">
        <a href="https://heyallergy.com">HeyAllergy.com</a> · Board-Certified Allergists
        <br />
        Questions?{shell.contactPrefix ? ' Contact us at ' : ' '}
        <a href="mailto:support@heyallergy.com">support@heyallergy.com</a>
      </div>
    </>
  );

  return (
    <ToastProvider>
      {/*
       * The payments runtime — the API client and the tokenizer, chosen together — is
       * installed here and nowhere else (M7.7). That makes "does this screen reach the
       * payment vendor?" a property of the app shell rather than of a component: a card
       * form rendered without this provider is inert, loads no script and makes no
       * request (`lib/api/payments-client-context.tsx`).
       */}
      {/*
       * The build identity is an attribute, not a badge. M2.6's placeholder printed it
       * in the footer; the live funnel has no such line, so printing it would be a
       * pixel difference for no patient benefit — while support still needs to know
       * which bundle a patient was on (MASTER_PLAN M0.7).
       */}
      {/*
       * Two improvements are pure CSS, so they are attributes on the funnel root rather
       * than branches in every component: `booking.css` carries one block per attribute
       * and neither block exists in the cascade when the flag is off.
       *   data-a11y="on"   — `funnel.a11yContrast`: the accessible colour set (README §6)
       *   data-mobile="polish" — `funnel.mobilePolish`: 44px targets, safe areas, the
       *                      pinned primary button; nothing above 767px.
       */}
      <div
        className="ha-funnel"
        data-font={dmSans ? 'dm-sans' : 'system'}
        data-a11y={accessiblePalette ? 'on' : undefined}
        data-mobile={flags.funnel.mobilePolish ? 'polish' : undefined}
        data-build={`${APP_BUILD.version}+${APP_BUILD.commit}`}
      >
        <PaymentsRuntimeProvider runtime={paymentsRuntime}>
          {/*
           * The intake runtime — the form client and the two object stores, chosen
           * together — is installed here for the same reason and on the same terms
           * (M8.7, `lib/api/intake-client-context.tsx`). A paperwork step or a card
           * upload rendered without it is inert and makes no request.
           */}
          <IntakeRuntimeProvider runtime={intakeRuntime}>
            {shell.embed ? <div className="ha-funnel-embed">{body}</div> : body}
          </IntakeRuntimeProvider>
        </PaymentsRuntimeProvider>
      </div>
    </ToastProvider>
  );
}
